import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import SensorTypeModel from '../../../../../services/sensorType.model';
import {SensorTypeService} from '../../../../../services/sensorType.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * SensorTypeComponent
 *
 * This component is responsible for managing Sensor Types within the Hardware Management module.
 * It provides a user interface for administrators to view, create, update, and search for sensor types.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all sensor types.
 * - Provide search functionality to filter sensor types by name or abbreviation.
 * - Manage the creation of new sensor types with validation.
 * - Handle updates to existing sensor types.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update sensor types.
 * - **Real-time Search**: Filter list by sensor type name or abbreviation.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures sensor type names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all sensor types from `SensorTypeService`.
 * 3. Formats dates using `moment`.
 * 4. Initializes `BatchLoader` with the full dataset.
 * 5. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 6. `addUpdateSensorType` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-sensor-type
 * @example
 * <app-sensor-type></app-sensor-type>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-sensor-type',
  templateUrl: './sensor-type.component.html',
  styleUrls: ['./sensor-type.component.scss']
})
export class SensorTypeComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched sensor types from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {SensorTypeModel[]}
   * @default []
   */
  public sensorTypes: SensorTypeModel[] = [];

  /**
   * Array holding the currently displayed sensor types.
   * This list is populated by the BatchLoader (subset of sensorTypes or filteredSensorTypes).
   * @type {SensorTypeModel[]}
   * @default []
   */
  public sensorTypesList: SensorTypeModel[] = [];

  /**
   * Model for the current sensor type being added or edited.
   * Bound to the form for data manipulation.
   * @type {SensorTypeModel}
   */
  public sensorType: SensorTypeModel = new SensorTypeModel();

  /**
   * Array holding sensor types that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {SensorTypeModel[]}
   * @default []
   */
  public filteredSensorTypes: SensorTypeModel[] = [];

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public sensorTypeDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for sensor type details (name, abbreviation, description).
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  enableDisablePageLoader: boolean;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for SensorTypeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Max 200 characters.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {SensorTypeService} sensorTypeService - Service for Sensor Type API operations.
   * @service SensorTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public sidenavaction: Sidenavaction, public sensorTypeService: SensorTypeService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.sensorType = new SensorTypeModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches all sensor types from the backend.
   * 5. Formats the 'updatedAt' date for each sensor type.
   * 6. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.search = '';
    this.sensorTypes = [];
    this.sensorTypesList = [];
    this.filteredSensorTypes = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.sensorTypes = await this.sensorTypeService.getAllSensorTypes();
      if (this.sensorTypes.length > 0) {
        this.sensorTypes.forEach(eachSensorType => {
          eachSensorType.updatedAt = JSON.parse(JSON.stringify(moment(eachSensorType.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSensorTypes : this.sensorTypes,
      (updatedList) => {
        this.sensorTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.sensorTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the sensor type list based on the search query.
   *
   * - Filters by name or abbreviation (case-insensitive).
   * - Updates `filteredSensorTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredSensorTypes`, `sensorTypesList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.sensorTypes.filter(route =>
        route.name?.toLowerCase().includes(query) || route.abbreviation?.toLowerCase().includes(query)
      )
      : this.sensorTypes;
    this.filteredSensorTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.sensorTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.registerForm.reset();
   this.showForm = false;
    this.sensorType = new SensorTypeModel();
    this.sensorTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Sensor Type form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateSensorType
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateSensorType() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.sensorTypeDoubleClick = true;
      if (this.submitButton) {
        await this.sensorTypeService.create(formData);
        this.toastrService.success('Sensor type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSensorType = {...formData}
        delete updateSensorType.id;
        await this.sensorTypeService.updateOneByID(this.sensorType.id, updateSensorType);
        $('#addModal').modal('hide');
        this.toastrService.success('Sensor type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.sensorTypeDoubleClick = false;
    }
  }

  /**
   * Retrieves a Sensor Type by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getSensorTypeById
   * @param {string} id - The ID of the sensor type to edit.
   * @returns {void}
   */
  public getSensorTypeById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.sensorTypeDoubleClick = false;
    this.updateButton = true;
    const foundSensorType = this.sensorTypesList.find(uom => uom?.id === id);
    this.sensorType = foundSensorType ? { ...foundSensorType } : new SensorTypeModel(); // clone object
    if (this.sensorType) {
      this.registerForm.patchValue({
        name: this.sensorType.name,
        abbreviation: this.sensorType.abbreviation,
        description: this.sensorType.description
      });
    }
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
